import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NavigationService } from '../../../../services/navigation.service';
import { HtmlDescriptionFieldComponent } from '../../../fields/standard/html-description-field/html-description-field.component';
import { NameFieldComponent } from '../../../fields/standard/name-field/name-field.component';
import { TextFieldComponent } from '../../../fields/standard/text-field/text-field.component';
import { Client, ClientText, ClientAccountType, _ } from '@mira-avant/mtlibs';
import { AppModule } from '../../../../modules/app.module';
import { ZorroModule } from '../../../../modules/zorro.module';
import { ThirdPartyModule } from '../../../../modules/third-party.module';
import { AngularModule } from '../../../../modules/angular.module';
import { TextareaFieldComponent } from '../../../fields/standard/textarea-field/textarea-field.component';
import { FormTitleComponent } from '../../../ui/forms/form-title/form-title.component';
import { CheckboxFieldComponent } from '../../../fields/standard/checkbox-field/checkbox-field.component';
import { PreviousImageSelectorComponent } from '../../../ui/admin/previous-image-selector/previous-image-selector.component';
import { AccountTypeFieldComponent } from '../../../fields/standard/account-type-field/account-type-field.component';

@Component({
	selector: 'app-client-form',
	standalone: true,
	imports: [
		AppModule,
		ZorroModule,
		ThirdPartyModule,
		AngularModule,
		NameFieldComponent,
		HtmlDescriptionFieldComponent,
		TextFieldComponent,
		TextareaFieldComponent,
		CheckboxFieldComponent,
		FormTitleComponent,
		PreviousImageSelectorComponent,
		AccountTypeFieldComponent,
	],
	templateUrl: './client-form.component.html',
	styleUrls: ['./client-form.component.scss'],
})
export class ClientFormComponent {
	@Input('client') public client: Client | undefined;
	@Input('editLanguage') public editLanguage: string | undefined;
	@Output('onSubmit') public onSubmitEmitter: EventEmitter<any> = new EventEmitter<any>();

	public showPreviousImageSelector: boolean = false;

	public form: FormGroup = this.fb.group({
		name: ['', NameFieldComponent.validators],
		logoUri: ['', []],
		descriptionHtml: ['', HtmlDescriptionFieldComponent.validators],
		descriptionShort: ['', TextFieldComponent.validators],
		subdomain: ['', TextFieldComponent.validators],
		accountType: [ClientAccountType.K12_SCHOOL, [Validators.required]],
		allowSectionSelfRostering: [false, []],
		allowCleverSso: [false, []],
		cleverSsoDistrictId: ['', []],
		allowEduskills: [false, []],
		eduskillsDistrictId: ['', []],
		dataJson: ['', []],
	});

	constructor(
		private fb: FormBuilder,
		public nav: NavigationService
	) {}

	public onSubmit() {
		this.onSubmitEmitter.emit(this.form.value);
	}

	public clear(): void {
		this.form.reset();
	}

	ngOnChanges(changes: SimpleChanges) {
		if (_.get(changes, 'editLanguage', undefined) || _.get(changes, 'client', undefined)) {
			if (changes['client']) {
				const newClient: Client | undefined = _.get(changes, 'client.currentValue', undefined) as
					| Client
					| undefined;
				this.client = newClient;
			}

			const newClientText: ClientText | undefined = this.client
				? this.client.client_text.find((text: ClientText) => text.default_lang_id === this.editLanguage)
				: undefined;

			this.form.patchValue({
				name: newClientText?.name || '',
				logoUri: newClientText?.logo_uri || '',
				descriptionHtml: newClientText?.description_html || '',
				descriptionShort: newClientText?.description_short || '',
				subdomain: this.client?.subdomain || '',
				accountType: this.client?.account_type || ClientAccountType.K12_SCHOOL,
				allowSectionSelfRostering: this.client?.allow_section_self_rostering || false,
				allowCleverSso: this.client?.allow_clever_sso || false,
				cleverSsoDistrictId: this.client?.clever_sso_district_id || '',
				allowEduskills: this.client?.allow_eduskills || false,
				eduskillsDistrictId: this.client?.eduskills_district_id || '',
				dataJson: this.client?.data_json ? JSON.stringify(this.client?.data_json) : '',
			});
		}
	}

	public selectLogoUri(logoUri: string): void {
		this.form.patchValue({ logoUri });
		this.showPreviousImageSelector = false;
	}
}
